import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { Company } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';
import { ApiResponse } from '@shared-models';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

/**
 * Tipo de retorno para paginación server-side
 */
export interface CompanyPageResponse {
  items: Company[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

@Injectable({ providedIn: 'root' })
export class CompanyService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/structure/companies`;
  }

  /**
   * Normaliza el status del API al modelo del frontend
   * API returns: "ACTIVE" | "INACTIVE"
   * Frontend: 'active' | 'inactive'
   */
  private normalizeStatus(status: string | undefined): 'active' | 'inactive' {
    if (!status) return 'active';
    return status.toUpperCase() === 'INACTIVE' ? 'inactive' : 'active';
  }

  /**
   * Normaliza el companyType del API al modelo del frontend.
   *
   * Prioridad:
   *   1) companyClassCode explícito (CON / INT) — el más fiable
   *   2) companyClassName ("Empresa concesionario" / "Empresa interna")
   *   3) presencia de parentCompanyId (solo aplica a concesionarias)
   *   4) default: 'propia'
   *
   * Importante: el GET by id a veces omite `companyClassName` o lo manda
   * con otra forma. Si degradamos a 'propia' por defecto, el form de
   * edición muestra CON un instante (dato del listado) y luego oculta
   * el select de empresa padre y desactiva Contrato/Garantía.
   */
  private normalizeCompanyType(item: {
    companyClassName?: string;
    companyClassCode?: string;
    companyClass?: { code?: string; name?: string; text?: string };
    parentCompanyId?: string;
  }): 'propia' | 'concesionaria' {
    const code = (
      item.companyClassCode ||
      item.companyClass?.code ||
      ''
    ).toUpperCase();
    // CON = Agente/concesionario en el catálogo actual.
    if (code === 'CON') return 'concesionaria';
    if (code === 'INT' || code === 'COR') return 'propia';

    const name = (
      item.companyClassName ||
      item.companyClass?.name ||
      item.companyClass?.text ||
      ''
    ).toUpperCase();
    if (name) {
      if (
        name.includes('CONCESION') ||
        name.includes('AGENTE') ||
        name === 'CON'
      ) {
        return 'concesionaria';
      }
      if (
        name.includes('INTERN') ||
        name.includes('EMPRESA') ||
        name.includes('CORRESPONSAL') ||
        name === 'INT' ||
        name === 'COR'
      ) {
        return 'propia';
      }
    }

    // Señal de negocio: solo las concesionarias/agentes tienen empresa padre.
    if (item.parentCompanyId) {
      return 'concesionaria';
    }

    return 'propia';
  }

  /**
   * Transforma la respuesta del backend al modelo del frontend
   * API fields (verificados contra response real de /structure/companies):
   *   - tradeName, legalName, groupName, companyClassName, countryName
   *   - countryLargeIso, status, branchs, agents, coorespondents, atm
   *   - boxs (cajas), vaults (bóvedas), duedate (vigencia contrato)
   *   - parentCompanyName (solo para empresas concesionarias)
   *   - groupId, countryId, provinceId, localityId, parentCompanyId,
   *     companyClassId, address, imageUrl, contacts
   *
   * NOTA sobre los nombres del API:
   *   - `branchs` (sin 'e') es typo del back, no del FE.
   *   - `boxs` (en lugar de "boxes") es typo del back.
   *   - `coorespondents` (en lugar de "correspondents") es typo del back.
   *   - `duedate` (en lugar de "dueDate") es typo del back.
   *   - `vaults` es la forma correcta.
   *   Se preservan los typos del back acá para no romper el binding, pero
   *   en el modelo `Company` se usan los nombres limpios.
   */
  private mapToCompany(item: any): Company {
    return {
      id: item.id,
      name: item.tradeName || item.name || '',
      tradeName: item.tradeName || item.name || '',
      groupName: item.groupName || '',
      legalName: item.legalName || '',
      code: item.code || '',
      countryName: item.countryName || '',
      country:
        item.countryName || item.countryLargeIso || item.country || '',
      address: item.address,
      website: item.website,
      branchesCount: item.branchs ?? item.branchesCount ?? 0,
      agentsCount: item.agents ?? item.agentsCount ?? 0,
      correspondentsCount: item.coorespondents ?? item.correspondentsCount ?? 0,
      atmCount: item.atm ?? item.atmCount ?? 0,
      status: this.normalizeStatus(item.status),
      companyType: this.normalizeCompanyType(item),
      companyClassCode: (
        item.companyClassCode ||
        item.companyClass?.code ||
        ''
      )
        .toString()
        .trim()
        .toUpperCase() || undefined,
      // Vigencia de contrato: la API manda string ISO o "N/A" cuando no aplica.
      // Fallback a string vacío si el back no manda el campo (defensa).
      contractExpiry: item.duedate || '',
      // Cajas (caja): la API usa el typo "boxs" (en lugar de "boxes").
      cajaCount: item.boxs ?? 0,
      // Bóvedas: la API usa "vaults" (correcto, sin typo).
      bovedaCount: item.vaults ?? 0,
      // Nombre de la empresa padre (concesionaria). Solo viene poblado
      // cuando companyType === 'concesionaria'; para empresas propias
      // el back NO devuelve el campo y queda como string vacío.
      parentCompanyName: item.parentCompanyName || '',
      // IDs crudos preservados para el form de view/edit
      groupId: item.groupId ?? item.group?.id,
      countryId: item.countryId ?? item.country?.id,
      provinceId: item.provinceId ?? item.province?.id,
      localityId: item.localityId ?? item.locality?.id,
      parentCompanyId: item.parentCompanyId ?? item.parentCompany?.id,
      companyClassId: item.companyClassId ?? item.companyClass?.id,
      codeCorrespondent:
        item.codeCorrespondent ?? item.code_correspondent ?? '',
      imageUrl: item.imageUrl || '',
      contacts: item.contacts,
    };
  }

  /**
   * Obtiene empresas con paginación server-side y filtro por type (INT = propia, CON = concesionario)
   */
  getAll(
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
    type?: string,
  ): Observable<CompanyPageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim());
    }
    if (type && type.trim()) {
      params = params.set('type', type.trim());
    }

    return this.httpClient
      .get<ApiResponse<PageableData>>(this.apiUrl, { params })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const items = pageableData?.content || [];
          return {
            items: items.map((item: any) => this.mapToCompany(item)),
            page: pageableData?.page ?? 0,
            size: pageableData?.size ?? size,
            totalElements: pageableData?.totalElements ?? 0,
            totalPages: pageableData?.totalPages ?? 0,
          };
        }),
      );
  }

  /**
   * Obtiene una empresa por ID
   */
  getById(id: string): Observable<Company> {
    return this.httpClient
      .get<ApiResponse<any>>(`${this.apiUrl}/${id}`)
      .pipe(map((response) => this.mapToCompany(response.data)));
  }

  /**
   * Serializa el payload SIEMPRE como FormData (multipart/form-data).
   *
   * ¿Por qué SIEMPRE FormData y no "JSON si no hay File"?
   * Porque el backend tiene `@PutMapping(consumes = MULTIPART_FORM_DATA_VALUE)`:
   * el PUT SOLO acepta multipart. Si mandamos JSON cuando no hay File nuevo,
   * el back tira "415 Unsupported Media Type — application/json is not supported".
   *
   * Reglas de serialización:
   *   - `null` / `undefined` → se omiten (no se appendean al FormData)
   *   - `File` / `Blob` → se appendean tal cual (es el campo `image` del back)
   *   - Array de objetos → se serializa con la NOTACIÓN DE BINDING DE SPRING
   *     (`key[i].field=value`). Esto es lo que `@ModelAttribute` espera
   *     cuando el campo destino es `List<DTO>` (ej. `CompanyRequest.contacts`).
   *     Si se mandara como JSON string (`contacts=[{...}]`), Spring NO podría
   *     hacer binding y tiraría `BindException` → `MethodArgumentNotValidException`
   *     → "Errores de validación en los datos enviados".
   *   - Array de primitivos → `key[i]=value`
   *   - Objeto (no array) → `key.field=value` recursivo
   *   - Primitivo → se convierte a string
   */
  private toFormData(data: Record<string, any>): FormData {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      this.appendToFormData(formData, key, value);
    }
    return formData;
  }

  /**
   * Helper recursivo que escribe un valor en un FormData respetando la
   * notación de binding de Spring para `@ModelAttribute`.
   *
   * Reglas (mismas que en `toFormData`):
   *   - File/Blob → append tal cual en la clave actual.
   *   - Array → si es de objetos, expande a `key[i].field=value`; si es de
   *     primitivos, expande a `key[i]=value`.
   *   - Objeto → expande a `key.field=value` recursivo.
   *   - null/undefined → se omiten.
   *   - Primitivo → `String(value)`.
   *
   * Ejemplo de salida para `contacts: [{name:'A', primary:true}]`:
   *   contacts[0].name=A
   *   contacts[0].primary=true
   */
  private appendToFormData(
    formData: FormData,
    key: string,
    value: unknown,
  ): void {
    if (value === null || value === undefined) return;

    if (value instanceof File || value instanceof Blob) {
      formData.append(key, value);
      return;
    }

    if (Array.isArray(value)) {
      // Spring binding: key[0].field=value para objetos, key[0]=value para primitivos.
      // Iteramos incluso si el array está vacío: el back puede tener validación
      // que requiera presencia. Si el array está vacío, simplemente no emitimos nada.
      value.forEach((item, index) => {
        if (item === null || item === undefined) return;
        if (
          typeof item === 'object' &&
          !(item instanceof File) &&
          !(item instanceof Blob)
        ) {
          // Array de objetos → notación `key[i].field=value`
          for (const [subKey, subValue] of Object.entries(
            item as Record<string, unknown>,
          )) {
            this.appendToFormData(
              formData,
              `${key}[${index}].${subKey}`,
              subValue,
            );
          }
        } else {
          // Array de primitivos → notación `key[i]=value`
          this.appendToFormData(formData, `${key}[${index}]`, item);
        }
      });
      return;
    }

    if (typeof value === 'object') {
      // Objeto plano → notación `key.field=value` recursivo
      for (const [subKey, subValue] of Object.entries(
        value as Record<string, unknown>,
      )) {
        this.appendToFormData(formData, `${key}.${subKey}`, subValue);
      }
      return;
    }

    // Primitivo (string, number, boolean, bigint)
    formData.append(key, String(value));
  }

  /**
   * Crea una nueva empresa.
   *
   * El backend NO devuelve el `Company` creado en el body. El `CompanyController`
   * hace `ApiResponse.created("Empresa creada exitosamente")` con `data: null`.
   * Y como `ApiResponse` tiene `@JsonInclude(NON_NULL)`, ese `data` se omite
   * del JSON de respuesta. Si acá llamáramos a `mapToCompany(response.data)`,
   * `response.data` sería `undefined` y reventaría con
   * "Cannot read properties of undefined (reading 'id')".
   *
   * Por eso retornamos `{ message }` y NO `Company`. Si en el futuro el back
   * cambia y empieza a devolver el `Company` creado, hay que actualizar este
   * método y volver a llamar a `mapToCompany` (con guard de null/undefined).
   */
  create(
    data: Partial<Company> & { image?: File | null },
  ): Observable<{ message: string }> {
    const formData = this.toFormData(data as Record<string, any>);
    return this.httpClient.post<ApiResponse<any>>(this.apiUrl, formData).pipe(
      map((response) => ({
        message: response.message || 'Empresa creada exitosamente',
      })),
    );
  }

  /**
   * Actualiza una empresa existente.
   *
   * Mismo razonamiento que `create()`: el back hace `ApiResponse.ok(null, "...")`,
   * `data` es `null` y se omite del JSON. No llamamos a `mapToCompany` para
   * evitar el TypeError. Retornamos solo el mensaje de confirmación.
   */
  update(
    id: string,
    data: Partial<Company> & { image?: File | null },
  ): Observable<{ message: string }> {
    const formData = this.toFormData(data as Record<string, any>);
    return this.httpClient
      .put<ApiResponse<any>>(`${this.apiUrl}/${id}`, formData)
      .pipe(
        map((response) => ({
          message: response.message || 'Empresa actualizada exitosamente',
        })),
      );
  }

  /**
   * Elimina una empresa
   */
  delete(id: string): Observable<{ message: string }> {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`)
      .pipe(
        map((response) => ({
          message: response.message || 'Empresa eliminada exitosamente',
        })),
      );
  }

  /**
   * Actualiza el status de una empresa
   */
  updateStatus(
    id: string,
    status: 'active' | 'inactive',
  ): Observable<{ message: string }> {
    const apiStatus = status === 'inactive' ? 'INACTIVE' : 'ACTIVE';
    return this.httpClient
      .patch<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${id}/status`, { status: apiStatus })
      .pipe(
        map((response) => ({
          message: response.message || 'Status actualizado exitosamente',
        })),
      );
  }

  // NOTA: getHistory() se movio a CompanyHistoryService
  // (services/structure/companies/company-history.service.ts)
  // Logica identica: GET /api/structure/companies/{id}/history paginado.

  /**
   * Exporta empresas a PDF
   */
  exportPdf(options?: {
    title?: string;
    status?: string;
    search?: string;
    type?: string;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de empresas')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('type', options?.type || '');

    return this.httpClient.get(`${this.apiConfig.apiUrl}/export/pdf/company`, {
      params,
      responseType: 'blob',
    });
  }

  /**
   * Exporta empresas a Excel
   */
  exportExcel(options?: {
    title?: string;
    status?: string;
    search?: string;
    type?: string;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de empresas')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('type', options?.type || '');

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/excel/company`,
      {
        params,
        responseType: 'blob',
      },
    );
  }
}
